import React from 'react';

import { buildReportCellHref, isUUIDV4, parseReportCellColumn } from '@aline/common';
import { link } from '@aline/routes';
import ReactMarkdown from 'react-markdown';
import { Citation, counterResetStyles } from 'src/components/ai/shared/components/Citation';
import { openDocumentQuestion, openLink, useDispatchInfoSnack } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  content: {
    ...counterResetStyles,
    display: 'block',
    textAlign: 'left',
  },
  line: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
  },
  link: {
    '&:hover': {
      opacity: 0.8,
    },
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
  markdown: {
    '& p': {
      margin: 0,
    },
    display: 'inline',
  },
}));

export interface AiReasoningTooltipData {
  aiRationale: string | null;
  aiReminderAutomation: {
    id: string;
    report: { id: string; name: string };
    title: string;
  } | null;
  columnMap: Map<string, string>;
  isAiGenerated: boolean;
  documentId?: string;
}

export function buildAiReasoningTooltipContent(data: AiReasoningTooltipData): React.ReactNode {
  return <AiReasoningTooltipContent data={data} />;
}

function createRationaleLink(
  columnMap: Map<string, string>,
  onUnavailableClick: () => void,
  reportId?: string,
  documentId?: string
) {
  return function RationaleLink({ children, href }: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
    if (href?.startsWith('#col/')) {
      const columnId = decodeURIComponent(href.substring('#col/'.length));
      const displayName = columnMap.get(columnId) ?? columnId;
      const isAIColumn = isUUIDV4(columnId);

      function handleClick(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (documentId && isAIColumn) {
          openDocumentQuestion({ documentId, questionId: columnId });
          return;
        }

        const column = parseReportCellColumn(columnId);
        if (reportId && documentId && column) {
          openLink(buildReportCellHref({ column, documentId, reportId }));
          return;
        }

        onUnavailableClick();
      }

      return <Citation tooltip={displayName} onClickCitation={handleClick} />;
    }

    return <a href={href}>{children}</a>;
  };
}

function TooltipLink({
  children,
  className,
  href,
}: {
  children: React.ReactNode;
  className: string;
  href: string | undefined;
}) {
  if (!href) return <>{children}</>;

  return (
    <a
      className={className}
      href={href}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        openLink(href);
      }}
    >
      {children}
    </a>
  );
}

function AiReasoningTooltipContent({ data }: { data: AiReasoningTooltipData }): JSX.Element | null {
  const { classes } = useStyles();
  const lines: React.ReactNode[] = [];
  const dispatchInfoSnack = useDispatchInfoSnack({ message: 'Unable to open citation source.' });

  if (data.aiReminderAutomation) {
    const reportHref = link.ReportDetail({ reportId: data.aiReminderAutomation.report.id });
    const automationHref = `${link.ReportReportDetailAutomations({ reportId: data.aiReminderAutomation.report.id })}?highlightAutomation=${data.aiReminderAutomation.id}`;

    lines.push(
      <span key="report" className={classes.line}>
        <strong>{`Report: `}</strong>
        <TooltipLink className={classes.link} href={reportHref}>
          {data.aiReminderAutomation.report.name}
        </TooltipLink>
      </span>
    );

    lines.push(
      <span key="automation" className={classes.line}>
        <strong>{`Automation: `}</strong>
        <TooltipLink className={classes.link} href={automationHref}>
          {data.aiReminderAutomation.title}
        </TooltipLink>
      </span>
    );
  }

  if (data.aiRationale) {
    const RationaleLink = createRationaleLink(
      data.columnMap,
      dispatchInfoSnack,
      data.aiReminderAutomation?.report?.id,
      data.documentId
    );
    lines.push(
      <span key="rationale" className={classes.line}>
        <strong>{`AI Reasoning: `}</strong>
        <ReactMarkdown className={classes.markdown} components={{ a: RationaleLink }}>
          {data.aiRationale}
        </ReactMarkdown>
      </span>
    );
  }

  if (!lines.length) return null;

  return <span className={classes.content}>{lines}</span>;
}
